import { useParams, Link, useLocation } from "wouter";
import { trpc } from "../lib/trpc";
import Header from "../components/Header";
import Footer from "../components/Footer";

const IMAGES = {
  entrance: "/store-entrance.jpg",
  seating: "/store-seating.jpg",
  aisle: "/store-aisle.jpg",
  restroom: "/store-restroom.jpg",
};

export default function StoreDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const storeId = parseInt(id ?? "0", 10);

  const { data: store, isLoading, error } = trpc.stores.getById.useQuery(
    { id: storeId },
    { enabled: storeId > 0 }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="pt-24 pb-12">
          <div className="container py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-6">
              <svg className="h-8 w-8 text-stone-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <circle cx="12" cy="6" r="3" />
                <path d="M12 9v4M8 21l2-8h4l2 8M6 13h12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-stone-900 mb-4">
              お店が見つかりませんでした
            </h1>
            <Link href="/stores" className="text-teal-600 hover:text-teal-700 font-medium">
              お店一覧に戻る
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Parse features and facilities
  const features = typeof store.features === 'string'
    ? JSON.parse(store.features || '[]')
    : (store.features || []);

  const facilities = typeof store.facilitiesJson === 'string'
    ? JSON.parse(store.facilitiesJson || '{}')
    : (store.facilitiesJson || {});

  // Calculate scores
  const accessLevel = store.accessLevel || store.entryDifficulty || 3;
  const noiseLevel = store.comfortNoiseLevel || Math.round((store.noiseLevel || 50) / 20) || 3;

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />

      {/* ヒーロー画像 */}
      <div className="relative pt-16 aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-stone-200">
        <img
          src={store.thumbnailUrl || IMAGES.entrance}
          alt={store.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        {/* オーバーレイ情報 */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="container max-w-5xl mx-auto">
            <span className="inline-block mb-3 px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium">
              {store.area}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 tracking-tight">
              {store.name}
            </h1>
            {store.nearestStation && (
              <p className="text-white/80 flex items-center gap-2">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {store.nearestStation}
                {store.walkingMinutes && ` 徒歩${store.walkingMinutes}分`}
              </p>
            )}
          </div>
        </div>
      </div>

      <main className="container max-w-5xl py-8 md:py-12">
        {/* スコアカード */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <svg className="h-5 w-5 text-amber-500 fill-amber-500" viewBox="0 0 24 24">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span className="text-sm font-medium text-stone-500">入りやすさ</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-stone-900">{accessLevel}</span>
              <span className="text-stone-400">/ 5</span>
            </div>
            <p className="text-xs text-stone-500 mt-2">
              {accessLevel >= 4 ? "とても入りやすい" : accessLevel === 3 ? "普通" : "やや難しい"}
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828-9.9a9 9 0 0112.728 0" />
              </svg>
              <span className="text-sm font-medium text-stone-500">居心地</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-stone-900">{noiseLevel}</span>
              <span className="text-stone-400">/ 5</span>
            </div>
            <p className="text-xs text-stone-500 mt-2">
              {noiseLevel >= 4 ? "賑やか・泣いてもOK" : noiseLevel >= 3 ? "適度な賑わい" : "静かめ"}
            </p>
          </div>
        </div>

        {/* 説明 */}
        {store.description && (
          <div className="mb-8">
            <p className="text-lg text-stone-600 leading-relaxed">{store.description}</p>
          </div>
        )}

        {/* クイックアクション */}
        <div className="flex gap-3 mb-10">
          <button
            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.address || store.name)}`, '_blank')}
            className="flex-1 h-14 rounded-2xl bg-stone-900 hover:bg-stone-800 text-white font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            地図・ルート案内
          </button>
          {store.phoneNumber && (
            <button
              onClick={() => window.open(`tel:${store.phoneNumber}`, '_self')}
              className="h-14 px-6 rounded-2xl border border-stone-200 hover:border-stone-300 transition-colors"
            >
              <svg className="h-5 w-5 text-stone-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </button>
          )}
        </div>

        {/* 入店・着席情報 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-stone-900 mb-6 tracking-tight">入店・着席ハードル</h2>

          {/* ベビーカー入店ステータス */}
          <div className="bg-teal-50 rounded-2xl p-6 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-teal-600 mb-1">ベビーカー入店</p>
                <p className="text-xl font-semibold text-teal-700">
                  {store.strollerEntry ? "そのまま入店OK" : "畳んで入店"}
                </p>
              </div>
              <svg className="h-10 w-10 text-teal-200" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <circle cx="12" cy="6" r="3" />
                <path d="M12 9v4M8 21l2-8h4l2 8M6 13h12" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>

          {/* 段差情報 */}
          {store.gapStatus && (
            <div className="bg-stone-50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3">
                <span className="text-stone-400">📐</span>
                <span className="text-sm text-stone-500 w-20">段差</span>
                <span className={`font-medium ${store.gapStatus.includes('なし') ? 'text-green-600' : 'text-stone-700'}`}>
                  {store.gapStatus}
                </span>
                {store.gapStatus.includes('なし') && <span className="ml-auto text-green-500">✓</span>}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FeatureItem label="エレベーター" value={store.hasElevator} highlight />
            <FeatureItem label="段差なし" value={!store.hasSteps} />
            <FeatureItem label="個室あり" value={store.hasPrivateRoom} />
            <FeatureItem label="座敷・小上がり" value={store.hasTatamiSeating} />
            <FeatureItem label="ソファ席" value={store.hasSofaSeating} />
            <FeatureItem label="ハイチェア" value={store.hasHighChair} />
          </div>

          {store.aisleWidth && (
            <div className="mt-4 p-4 bg-stone-50 rounded-xl">
              <p className="text-sm text-stone-500">通路幅</p>
              <p className="font-medium text-stone-900">{store.aisleWidth}</p>
            </div>
          )}

          {facilities.elevator_congestion && (
            <div className="mt-4 p-4 bg-stone-50 rounded-xl">
              <p className="text-sm text-stone-500">エレベーター混雑度</p>
              <p className="font-medium text-stone-900">{facilities.elevator_congestion}</p>
            </div>
          )}
        </section>

        {/* 設備・アメニティ */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-stone-900 mb-6 tracking-tight">設備・アメニティ</h2>

          <div className="space-y-6">
            {/* オムツ・授乳 */}
            <div>
              <h3 className="text-sm font-medium text-stone-400 mb-3 uppercase tracking-wider">オムツ・授乳</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FeatureItem label="オムツ替えスペース" value={store.hasDiaperChanging || facilities.diaper_changing} highlight />
                <FeatureItem label="授乳室" value={store.hasNursingRoom || facilities.nursing_room} />
              </div>
              {facilities.baby_chair && (
                <div className="mt-3 p-4 bg-stone-50 rounded-xl">
                  <p className="text-sm text-stone-500">椅子</p>
                  <p className="font-medium text-stone-900">{facilities.baby_chair}</p>
                </div>
              )}
              {facilities.stroller_parking && (
                <div className="mt-3 p-4 bg-stone-50 rounded-xl">
                  <p className="text-sm text-stone-500">ベビーカー置き場</p>
                  <p className="font-medium text-stone-900">{facilities.stroller_parking}</p>
                </div>
              )}
            </div>

            {/* 食事関連 */}
            <div>
              <h3 className="text-sm font-medium text-stone-400 mb-3 uppercase tracking-wider">食事関連</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FeatureItem label="離乳食持ち込みOK" value={store.babyFoodAllowed} highlight />
                <FeatureItem label="離乳食温め直し" value={store.babyFoodHeating} />
                <FeatureItem label="キッズメニュー" value={store.hasKidsMenu} />
                <FeatureItem label="アレルギー表示" value={store.hasAllergyInfo} />
              </div>
            </div>
          </div>

          {/* おすすめ時間帯 */}
          {facilities.recommended_time && (
            <div className="mt-6 p-4 rounded-xl bg-teal-50">
              <p className="text-sm font-medium text-teal-800">
                💡 おすすめ: <span className="font-bold">{facilities.recommended_time}</span>
              </p>
            </div>
          )}
        </section>

        {/* 店舗画像 */}
        {store.images && store.images.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-stone-900 mb-6 tracking-tight">店舗画像</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {store.images.map((image: any) => (
                <div key={image.id} className="group relative aspect-square overflow-hidden rounded-2xl bg-stone-200">
                  <img
                    src={image.imageUrl}
                    alt={image.caption || image.imageType}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="inline-block px-3 py-1 bg-white/20 text-white text-xs rounded-full backdrop-blur-sm">
                      {image.imageType}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 基本情報 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-stone-900 mb-6 tracking-tight">基本情報</h2>

          <div className="space-y-1">
            {store.address && (
              <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-stone-50 transition-colors">
                <svg className="h-5 w-5 text-stone-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-stone-500">住所</p>
                  <p className="font-medium text-stone-900">{store.address}</p>
                </div>
              </div>
            )}

            {store.isRainyDayOk && (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-lg">☔</span>
                </div>
                <div>
                  <p className="font-medium text-stone-900">雨の日OK</p>
                  <p className="text-sm text-stone-500">駅直結・地下道でアクセス可能</p>
                </div>
              </div>
            )}

            {store.phoneNumber && (
              <a href={`tel:${store.phoneNumber}`} className="flex items-start gap-4 p-4 rounded-xl hover:bg-stone-50 transition-colors group">
                <svg className="h-5 w-5 text-stone-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-stone-500">電話番号</p>
                  <p className="font-medium text-teal-600 group-hover:underline">{store.phoneNumber}</p>
                </div>
                <svg className="h-5 w-5 text-stone-300" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </a>
            )}

            {store.website && (
              <a href={store.website} target="_blank" rel="noopener noreferrer" className="flex items-start gap-4 p-4 rounded-xl hover:bg-stone-50 transition-colors group">
                <svg className="h-5 w-5 text-stone-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-stone-500">ウェブサイト</p>
                  <p className="font-medium text-teal-600 group-hover:underline truncate">{store.website}</p>
                </div>
                <svg className="h-5 w-5 text-stone-300 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </a>
            )}

            {store.openingHours && (
              <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-stone-50 transition-colors">
                <svg className="h-5 w-5 text-stone-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-stone-500">営業時間</p>
                  <p className="font-medium text-stone-900 whitespace-pre-line">{store.openingHours}</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* タグ */}
        {features.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10">
            {features.map((f: string) => (
              <span key={f} className="px-3 py-1 rounded-full bg-stone-100 text-stone-600 text-sm">
                {f}
              </span>
            ))}
          </div>
        )}

        {/* フッターアクション */}
        <div className="flex gap-3 pt-6 border-t border-stone-200">
          <button
            onClick={() => setLocation("/stores")}
            className="flex-1 h-14 rounded-2xl border border-stone-200 hover:border-stone-300 text-stone-700 font-medium transition-colors"
          >
            店舗一覧に戻る
          </button>
          <button
            onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(store.address || store.name)}`, '_blank')}
            className="flex-1 h-14 rounded-2xl bg-stone-900 hover:bg-stone-800 text-white font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            地図で見る
          </button>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function FeatureItem({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: boolean | number | null | undefined;
  highlight?: boolean;
}) {
  const isAvailable = value === true || value === 1;
  return (
    <div className={`flex items-center justify-between py-3 px-4 rounded-xl ${highlight && isAvailable ? 'bg-teal-50' : 'bg-stone-50'}`}>
      <span className="text-sm font-medium text-stone-700">{label}</span>
      {isAvailable ? (
        <div className="flex items-center gap-1.5 text-emerald-600">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-medium">あり</span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-stone-400">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span className="text-sm">なし</span>
        </div>
      )}
    </div>
  );
}
